if (!customElements.get('c-product-tabs-v2')) {
  customElements.define('c-product-tabs-v2', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.tabs = [...this.querySelectorAll('[data-tab]')];
      this.panels = [...this.querySelectorAll('[data-panel]')];
      this.mobileSelect = this.querySelector('[data-tabs-select]');
      this.forms = new Map();
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: this.dataset.currency,
      });

      this.querySelectorAll('[data-tabs-form]').forEach((form) => {
        const data = JSON.parse(form.querySelector('[data-product-data]').textContent);
        this.forms.set(form, { data, busy: false, initialized: false });
        this.updateForm(form);
      });

      this.addEventListener('click', (event) => {
        const submit = event.target.closest('[data-submit]');
        const form = submit?.closest('[data-tabs-form]');
        if (form && this.forms.has(form)) {
          event.preventDefault();
          event.stopPropagation();
          this.addToCart(form);
          return;
        }
        const tab = event.target.closest('[data-tab]');
        if (tab) this.activateTab(tab.dataset.tab);
      }, { signal, capture: true });
      this.addEventListener('keydown', (event) => {
        const tab = event.target.closest('[data-tab]');
        if (!tab) return;
        let index = this.tabs.indexOf(tab);
        if (event.key === 'ArrowRight') index = (index + 1) % this.tabs.length;
        else if (event.key === 'ArrowLeft') index = (index - 1 + this.tabs.length) % this.tabs.length;
        else if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = this.tabs.length - 1;
        else return;
        event.preventDefault();
        this.activateTab(this.tabs[index].dataset.tab);
        this.tabs[index].focus();
      }, { signal });
      this.addEventListener('change', (event) => {
        if (event.target === this.mobileSelect) {
          this.activateTab(this.mobileSelect.value);
          return;
        }
        const form = event.target.closest('[data-tabs-form]');
        if (form && this.forms.has(form)) this.updateForm(form);
      }, { signal });
      this.addEventListener('submit', (event) => {
        if (!this.forms.has(event.target)) return;
        event.preventDefault();
        event.stopPropagation();
        this.addToCart(event.target);
      }, { signal, capture: true });
      document.addEventListener('shopify:block:select', (event) => {
        if (!this.contains(event.target)) return;
        const panel = event.target.closest('[data-panel]');
        if (panel) this.activateTab(panel.dataset.panel);
      }, { signal });
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    activateTab(id) {
      if (!this.panels.some((panel) => panel.dataset.panel === id)) return;
      this.panels.forEach((panel) => { panel.hidden = panel.dataset.panel !== id; });
      this.tabs.forEach((tab) => {
        const selected = tab.dataset.tab === id;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      if (this.mobileSelect) this.mobileSelect.value = id;
    }

    selectedVariants(form, data) {
      return data.products.map((product) => {
        const input = form.querySelector(`[data-product-index="${product.index}"]`);
        return product.variants.find((variant) => String(variant.id) === input.value);
      });
    }

    availablePlans(data, variant) {
      if (!data.subscriptionEnabled || data.products.length !== 1 || !variant) return [];
      return data.plans.flatMap((plan) => {
        const allocation = variant.selling_plan_allocations.find((item) => item.selling_plan_id === plan.id);
        return allocation ? [{ ...plan, allocation }] : [];
      });
    }

    updateForm(form) {
      const state = this.forms.get(form);
      const { data } = state;
      const variants = this.selectedVariants(form, data);
      const primary = variants[0];
      const plans = this.availablePlans(data, primary);
      const planSelect = form.querySelector('[data-plan-select]');
      const selectedPlanId = planSelect.value;
      planSelect.replaceChildren(...plans.map((plan) => new Option(plan.name, String(plan.id))));
      if (plans.some((plan) => String(plan.id) === selectedPlanId)) planSelect.value = selectedPlanId;
      const plan = plans.find((item) => String(item.id) === planSelect.value);
      const subscription = form.querySelector('[value="subscription"]');
      const onetime = form.querySelector('[value="onetime"]');
      const requiresPlan = data.products.some((product) => product.requiresSellingPlan);

      if (!state.initialized) subscription.checked = Boolean(plan && (data.defaultSubscription || requiresPlan));
      if (!plan) subscription.checked = false;
      if (requiresPlan && plan) subscription.checked = true;
      onetime.checked = !subscription.checked && !requiresPlan;
      subscription.disabled = !plan;
      onetime.disabled = requiresPlan;
      form.querySelector('[data-subscription-option]').hidden = !plan;
      form.querySelector('[data-onetime-option]').hidden = requiresPlan;
      form.querySelector('[data-subscription-details]').hidden = !plan;
      form.querySelector('[data-frequency]').hidden = !subscription.checked || plans.length < 2;

      const sellingPlan = form.querySelector('[data-selling-plan]');
      sellingPlan.disabled = !subscription.checked;
      sellingPlan.value = subscription.checked ? String(plan.id) : '';
      const total = variants.reduce((sum, variant) => sum + (variant?.price || 0), 0);
      const price = subscription.checked ? plan.allocation.price : total;
      const format = (amount) => this.money.format(amount / 100);
      const panel = form.closest('[data-panel]');
      panel.querySelector('[data-total-price]').textContent = format(total);
      form.querySelector('[data-onetime-price]').textContent = format(total);
      form.querySelector('[data-subscription-price]').textContent = plan ? format(plan.allocation.price) : '';
      const saving = form.querySelector('[data-saving]');
      const percent = plan && primary.price > 0 ? Math.round((primary.price - plan.allocation.price) / primary.price * 100) : 0;
      saving.hidden = percent <= 0;
      saving.textContent = `${data.strings.save} ${percent}%`;

      const paymentTerms = form.querySelector('[data-payment-terms]');
      if (paymentTerms) paymentTerms.hidden = subscription.checked;
      const unavailablePlan = requiresPlan && (!plan || data.products.length > 1);
      const available = variants.every((variant) => variant?.available) && !unavailablePlan;
      state.available = available;
      state.initialized = true;
      const button = form.querySelector('[data-submit]');
      button.disabled = state.busy || !available;
      if (!state.busy) {
        button.textContent = unavailablePlan
          ? data.strings.subscriptionUnavailable
          : !available ? data.strings.soldOut
            : `${subscription.checked ? data.subscriptionButtonLabel : data.buttonLabel} \u2022 ${format(price)}`;
      }
    }

    async addToCart(form) {
      const state = this.forms.get(form);
      if (state.busy) return;
      this.updateForm(form);
      const status = form.querySelector('[data-status]');
      status.removeAttribute('data-error');
      status.textContent = '';
      if (!state.available) {
        status.setAttribute('data-error', '');
        status.textContent = form.querySelector('[data-submit]').textContent;
        return;
      }

      const variants = this.selectedVariants(form, state.data);
      const sellingPlan = form.querySelector('[data-selling-plan]');
      const items = variants.map((variant) => ({ id: variant.id, quantity: 1 }));
      if (!sellingPlan.disabled) items[0].selling_plan = Number(sellingPlan.value);
      state.busy = true;
      form.setAttribute('aria-busy', 'true');
      form.querySelector('[data-submit]').textContent = state.data.strings.adding;
      form.querySelector('[data-submit]').disabled = true;
      const root = window.Shopify?.routes?.root || '/';

      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : state.data.strings.error);
        status.textContent = state.data.strings.added;
        await window.StepLabsSidecart.open(state.data.strings.sidecartError);
      } catch (error) {
        console.error('[c-product-tabs-v2] Add to cart failed:', error);
        status.setAttribute('data-error', '');
        status.textContent = error instanceof Error ? error.message : state.data.strings.error;
      } finally {
        state.busy = false;
        form.removeAttribute('aria-busy');
        this.updateForm(form);
      }
    }
  });
}
